import { inject, Injectable } from '@angular/core';
import { filter, map, Observable, share } from 'rxjs';
import { SseClient } from './sse-client.service';
import { ConfigService } from '../config/config.service';
import { BackendUrlConfig } from '../config/backend-url-config';

/**
 * Named events on the eingang stream. Consumers switch on these and ignore everything else, so the
 * backend can add event types without breaking anyone.
 */
export const EingangEventName = {
  CHANGED: 'eingang-changed',
} as const;

/**
 * Fired whenever an Eingang's status or assigned user changed — including when the assignment was
 * *removed*, because a takeover reaches ps-server as remove-then-set (see `.dev/DECISIONS.md` D6).
 */
export interface EingangChangedEvent {
  eingangId: string;
}

@Injectable({
  providedIn: 'root',
})
export class EingangEventsService {
  private readonly sseClient = inject(SseClient);
  private readonly configService = inject(ConfigService<BackendUrlConfig>);

  readonly eingangChanged$: Observable<EingangChangedEvent> = this.sseClient
    .connect(`${this.configService.config.psServerUrl}/api/v1/eingaenge/live`)
    .pipe(
      filter((frame) => frame.event === EingangEventName.CHANGED),
      map((frame) => parseEingangChanged(frame.data)),
      filter((event): event is EingangChangedEvent => event !== null),
      share(),
    );
}

function parseEingangChanged(data: string): EingangChangedEvent | null {
  try {
    const parsed = JSON.parse(data) as Partial<EingangChangedEvent>;
    return parsed.eingangId ? { eingangId: parsed.eingangId } : null;
  } catch {
    return null;
  }
}
